import {
  ChartConfig,
  ChartInputData,
  ParsedPeriod,
  Period,
  PeriodChartDataOptions,
  PeriodConfig,
  PeriodValue,
  ProcessedDataPoint,
} from '#components/chart/types';
import { roundToNearest } from '#lib/numberUtils';
import { getNiceTickValues } from 'recharts-scale';
import { htmlToUnicode } from '.';

const DEFAULT_PERIOD_CHART_OPTIONS: Required<PeriodChartDataOptions> = {
  gapBetweenTarget: 4,
  allowTargetDataOverlap: false,
};

export const enum TargetValueIndicator {
  DoesNotIncrease = 'Ei suurene',
  DoesNotDecrease = 'Ei vähene',
  Increases = 'Kasvab',
  NA = 'N/A',
  SameLevel = 'Samal tasemel',
  Decreases = 'Väheneb',
}

/**
 * Parses chart input data into a structured format suitable for rendering with Recharts library.
 *
 * @template T - Type extending ChartInputData
 * @param periodData - The input data containing real and target periods
 * @param options - Optional configuration to modify parsing behavior
 * @returns An object containing parsed chart data and configuration details
 */
export const parseChartData = <T extends ChartInputData>(
  periodData: T,
  options?: PeriodChartDataOptions,
) => {
  const { allowTargetDataOverlap, gapBetweenTarget } = {
    ...DEFAULT_PERIOD_CHART_OPTIONS,
    ...options,
  };

  const dataMap: Record<string, ProcessedDataPoint> = {};
  const config: ChartConfig = {
    maxYaxis: 0,
    minYaxis: Infinity,
    maxPeriod: 0,
    minPeriod: Infinity,
  };

  const realPeriodConfig: PeriodConfig = {
    max: 0,
    min: 0,
    lastIndex: -1,
  };

  const targetPeriodConfig: PeriodConfig = {
    max: 0,
    min: 0,
    lastIndex: -1,
  };

  periodData.realPeriods.sort(compareByValue);

  if (periodData.targetLevel && periodData.targetLevelDate) {
    const period = periodData.targetLevelDate.slice(0, 4);
    const value = periodData.targetLevel;

    targetPeriodConfig.descriptorText =
      periodData.targetLevelDescription ?? undefined;
    targetPeriodConfig.descriptorPoint = {
      x: Number(period),
      y: undefined,
    };

    if (
      !periodData.targetPeriods.find(
        (item) => item.period === period && item.value === value,
      )
    ) {
      periodData.targetPeriods.push({
        period,
        value: value,
      });
    }
  } else {
    const lastTargetPeriod =
      periodData.targetPeriods[periodData.targetPeriods.length - 1];
    if (lastTargetPeriod) {
      targetPeriodConfig.descriptorText =
        periodData.targetLevelDescription ?? undefined;

      targetPeriodConfig.descriptorPoint = {
        x: Number(lastTargetPeriod.period),
        y: undefined,
      };
    }
  }

  periodData.targetPeriods.sort(compareByValue);

  const hasRealData = periodData.realPeriods.length > 0;
  const hasTargetData = periodData.targetPeriods.length > 0;

  if (hasRealData) {
    processRealPeriods(periodData, dataMap, config, realPeriodConfig);
    targetPeriodConfig.lastIndex = realPeriodConfig.lastIndex;
    resetPeriodBounds(config);
  }

  if (hasTargetData) {
    processTargetPeriods(
      periodData,
      dataMap,
      config,
      targetPeriodConfig,
      realPeriodConfig,
      hasRealData,
      allowTargetDataOverlap,
    );
  }

  // Convert the map to a sorted array and add padding points
  const data = createSortedDataArray(
    dataMap,
    realPeriodConfig,
    targetPeriodConfig,
    hasRealData,
    gapBetweenTarget,
  );

  return createChartConfiguration(
    config,
    data,
    periodData,
    realPeriodConfig,
    targetPeriodConfig,
    hasRealData,
    hasTargetData,
  );
};

function processRealPeriods(
  periodData: ChartInputData,
  dataMap: Record<string, ProcessedDataPoint>,
  config: ChartConfig,
  realPeriodConfig: PeriodConfig,
) {
  // First pass: add all real data points
  const realDataPoints: Array<{
    period: number;
    value: number;
    descriptor: string | null;
  }> = [];

  periodData.realPeriods.forEach((item, index) => {
    const { period, descriptor } = parsePeriod(item.period);
    if (period == null) return;

    realDataPoints.push({ period, value: item.value, descriptor });
    updateConfig(config, period, item.value);

    if (!dataMap[period]) {
      realPeriodConfig.lastIndex++;
    }

    const isLast = index === periodData.realPeriods.length - 1;
    const isSecondToLast = index === periodData.realPeriods.length - 2;

    dataMap[period] = {
      targetValue: null,
      descriptor,
      xAxis: period,
      hideLabel: isSecondToLast,
      realValue: item.value,
      forceShow: isLast,
      isLast,
      isInterpolated: false,
    };
  });

  // Second pass: interpolate missing years between real data points
  realDataPoints.sort((a, b) => a.period - b.period);

  for (let i = 0; i < realDataPoints.length - 1; i++) {
    const current = realDataPoints[i];
    const next = realDataPoints[i + 1];
    const yearDiff = next.period - current.period;

    // If there are missing years between consecutive data points
    if (yearDiff > 1) {
      const valueDiff = next.value - current.value;
      const valueStep = valueDiff / yearDiff;

      // Add interpolated points for each missing year
      for (let year = current.period + 1; year < next.period; year++) {
        const interpolatedValue =
          current.value + (year - current.period) * valueStep;

        if (!dataMap[year]) {
          realPeriodConfig.lastIndex++;
        }

        dataMap[year] = {
          targetValue: null,
          descriptor: null,
          xAxis: year,
          hideLabel: false,
          realValue: interpolatedValue,
          forceShow: false,
          isLast: false,
          isInterpolated: true,
        };

        updateConfig(config, year, interpolatedValue);
      }
    }
  }

  realPeriodConfig.max = config.maxPeriod;
  realPeriodConfig.min = config.minPeriod;
}

function processTargetPeriods(
  periodData: ChartInputData,
  dataMap: Record<string, ProcessedDataPoint>,
  config: ChartConfig,
  targetPeriodConfig: PeriodConfig,
  realPeriodConfig: { max: number },
  hasRealData: boolean,
  allowTargetDataOverlap: boolean,
) {
  periodData.targetPeriods.forEach((item, index) => {
    const { period, descriptor } = parsePeriod(item.period);
    if (period == null) return;

    let targetValueIndicator: TargetValueIndicator | null = null;
    let targetValue: number | null;

    if (
      item.value === TargetValueIndicator.Increases ||
      item.value === TargetValueIndicator.Decreases ||
      item.value === TargetValueIndicator.SameLevel ||
      item.value === TargetValueIndicator.DoesNotIncrease ||
      item.value === TargetValueIndicator.DoesNotDecrease
    ) {
      targetValueIndicator = item.value;
      targetValue = periodData.realPeriods[0]?.value
        ? Number(periodData.realPeriods[0].value)
        : null;
    } else if (item.value === TargetValueIndicator.NA) {
      return;
    } else {
      const val = Number(item.value);
      targetValue = Number.isNaN(val) ? null : val;
    }

    if (
      !allowTargetDataOverlap &&
      hasRealData &&
      realPeriodConfig.max >= period
    ) {
      return;
    }

    if (
      targetValueIndicator != null &&
      period !== targetPeriodConfig.descriptorPoint?.x
    ) {
      return;
    } else {
      targetPeriodConfig.descriptorPoint = {
        x: period,
        y: targetValue ?? undefined,
        targetValueIndicator: targetValueIndicator,
      };
    }

    updateConfig(config, period, targetValue);

    if (!dataMap[period]) {
      targetPeriodConfig.lastIndex++;
    }

    const isLast = index === periodData.targetPeriods.length - 1;
    const isSecondLast = index === periodData.targetPeriods.length - 2;

    dataMap[period] = {
      ...dataMap[period],
      xAxis: period,
      forceShow: isLast,
      hideLabel: isSecondLast || index === 0,
      descriptor,
      targetValue: targetValue,
      targetValueIndicator,
      isLast,
    };
  });

  targetPeriodConfig.max = config.maxPeriod;
  targetPeriodConfig.min = config.minPeriod;
}

/**
 * Converts the data map into a sorted array and adds padding points and spacing if needed.
 */
function createSortedDataArray(
  dataMap: Record<string, ProcessedDataPoint>,
  realPeriodConfig: PeriodConfig,
  targetPeriodConfig: PeriodConfig,
  hasRealData: boolean,
  gapBetweenTarget: number,
) {
  const data = Object.values(dataMap).sort(
    (a, b) => Number(a.xAxis) - Number(b.xAxis),
  );

  // Add padding points and spacing
  addPaddingPoints(data, realPeriodConfig, targetPeriodConfig, hasRealData);
  addSpacingIfNeeded(
    data,
    realPeriodConfig,
    targetPeriodConfig,
    hasRealData,
    gapBetweenTarget,
  );

  return data;
}

/**
 * Parses a period string into its numeric value and descriptor.
 *
 * @param period - The period string to parse (e.g., "2035", "2035 sihttase" or "2035 rahaline eesmärk")
 * @returns An object containing the numeric period and its descriptor
 */
const parsePeriod = (period: string): ParsedPeriod => {
  const trimmedPeriod = period.trim();

  const numericValue = Number(trimmedPeriod);
  if (!isNaN(numericValue)) {
    return { period: numericValue, descriptor: null };
  }

  const [valueStr, ...descriptorParts] = trimmedPeriod.split(' ');
  const value = Number(valueStr);

  if (isNaN(value)) {
    console.warn('Invalid period value:', period);
    return { period: null, descriptor: null };
  }

  const descriptor = descriptorParts.join(' ').trim() ?? null;

  return { period: value, descriptor };
};

function updateConfig(
  config: ChartConfig,
  period: number,
  value: number | null,
) {
  if (value !== null) {
    config.maxYaxis = Math.max(config.maxYaxis, value);
    config.minYaxis = Math.min(config.minYaxis, value);
  }
  config.maxPeriod = Math.max(config.maxPeriod, period);
  config.minPeriod = Math.min(config.minPeriod, period);
}

function resetPeriodBounds(config: ChartConfig) {
  config.maxPeriod = 0;
  config.minPeriod = Infinity;
}

function compareByValue<T extends Period<PeriodValue>>(a: T, b: T) {
  return a.period.localeCompare(b.period, undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

/**
 * Creates the final chart configuration object suitable for rendering with Recharts library.
 */
function createChartConfiguration(
  config: ChartConfig,
  data: ProcessedDataPoint[],
  periodData: ChartInputData,
  realPeriodConfig: PeriodConfig,
  targetPeriodConfig: PeriodConfig,
  hasRealData: boolean,
  hasTargetData: boolean,
) {
  const roundedMaxYaxis = roundToNearest(config.maxYaxis, 2);
  const yAxisTicks = getNiceTickValues([
    config.minYaxis < 0 ? config.minYaxis : 0,
    roundedMaxYaxis,
  ]);
  const tickStep = yAxisTicks[1] - yAxisTicks[0];
  const lastTick = yAxisTicks[yAxisTicks.length - 1];

  if (tickStep > lastTick - config.maxYaxis) {
    yAxisTicks.push(lastTick + tickStep);
  }

  return {
    isInvalid: !hasTargetData && !hasRealData,
    unit: htmlToUnicode(periodData.unit || ''),
    targetIndicatorDirection: periodData.positiveDirection,
    maxYaxis: config.maxYaxis,
    minYaxis: config.minYaxis,
    minYAxisTick: yAxisTicks[0],
    maxYAxisTick: yAxisTicks[yAxisTicks.length - 1],
    yAxisTicks,
    hasRealData,
    min: Math.min(realPeriodConfig.min, targetPeriodConfig.min) - 1,
    max: Math.max(realPeriodConfig.max, targetPeriodConfig.max) + 1,
    realPeriodConfig,
    targetPeriodConfig,
    chartData: data,
    targetLevelDescription: periodData.targetLevelDescription,
    positiveDirection: periodData.positiveDirection,
  };
}

/**
 * Adds padding points before and after the data range
 */
function addPaddingPoints(
  data: ProcessedDataPoint[],
  realPeriodConfig: PeriodConfig,
  targetPeriodConfig: PeriodConfig,
  hasRealData: boolean,
) {
  // Add padding point before the minimum period
  const initialPadding: ProcessedDataPoint = {
    xAxis: Math.min(realPeriodConfig.min, targetPeriodConfig.min) - 1,
    targetValue: null,
    realValue: null,
    show: false,
  };
  data.unshift(initialPadding);

  // Update lastIndex based on the added padding
  if (hasRealData) {
    if (realPeriodConfig.min > targetPeriodConfig.min) {
      targetPeriodConfig.lastIndex++;
    } else {
      realPeriodConfig.lastIndex++;
    }
  } else {
    targetPeriodConfig.lastIndex++;
  }

  // Add padding point after the maximum period
  const finalPadding: ProcessedDataPoint = {
    xAxis: Math.max(realPeriodConfig.max, targetPeriodConfig.max) + 1,
    targetValue: null,
    realValue: null,
    show: false,
  };
  data.push(finalPadding);
  targetPeriodConfig.lastIndex++;
}

/**
 * Adds spacing between real and target data if needed
 */
function addSpacingIfNeeded(
  data: ProcessedDataPoint[],
  realPeriodConfig: PeriodConfig,
  targetPeriodConfig: PeriodConfig,
  hasRealData: boolean,
  gapBetweenTarget: number,
) {
  if (
    !hasRealData ||
    targetPeriodConfig.lastIndex - 1 !== realPeriodConfig.lastIndex
  ) {
    return;
  }

  const spaceValues =
    Math.min(targetPeriodConfig.max - realPeriodConfig.max, gapBetweenTarget) -
    1;

  if (spaceValues <= 0) {
    return;
  }

  // Create spacing data points
  const spacingPoints: ProcessedDataPoint[] = Array.from(
    { length: spaceValues },
    (_, index) => ({
      xAxis: realPeriodConfig.max + index + 1,
      targetValue: null,
      realValue: null,
      hideLabel: true,
      show: false,
    }),
  );

  // Insert spacing points before the last two elements
  data.splice(data.length - 2, 0, ...spacingPoints);

  // Update the lastIndex to account for added spacing
  targetPeriodConfig.lastIndex += spaceValues;
}
